import React, { useState, useEffect } from 'react'
import md5 from 'md5'
import api from '../../utils/apiClient'
import outlookIcon from '../../media/outlook.svg'
import gmailIcon from '../../media/google.svg'
import {
	Transition,
	MenuButton,
	MenuItems,
	MenuItem,
	Menu,
} from '@headlessui/react'
import {
	XMarkIcon,
	ChevronDownIcon,
	BellIcon,
} from '@heroicons/react/24/outline'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const EmailAccountList = ({
	accounts,
	user,
	onReAuthorization,
	onDeleteAccount,
}) => {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [deletingAccountId, setDeletingAccountId] = useState(null)
	const [error, setError] = useState(null)
	const [showError, setShowError] = useState(false)
	const [telegramConnections, setTelegramConnections] = useState([])
	const [telegramSettings, setTelegramSettings] = useState({})
	const [unifiedNotificationSettings, setUnifiedNotificationSettings] =
		useState({})
	const [showNotificationModal, setShowNotificationModal] = useState(false)
	const [currentAccountId, setCurrentAccountId] = useState(null)
	const [currentAccount, setCurrentAccount] = useState(null)

	const isDevUser = user?.userType === 'dev'

	useEffect(() => {
		if (isDevUser) {
			fetchTelegramConnections()
		}
	}, [isDevUser])

	useEffect(() => {
		if (accounts && accounts.length > 0) {
			const initialSettings = {}
			const initialUnifiedSettings = {}
			accounts.forEach(account => {
				if (account.telegramNotificationId) {
					initialSettings[account._id] = account.telegramNotificationId
				}

				// Initialize unified notification settings
				initialUnifiedSettings[account._id] = {
					telegram: {
						enabled:
							account.notificationSettings?.telegram?.enabled !== undefined
								? account.notificationSettings.telegram.enabled
								: !!account.telegramNotificationId,
						connectionId:
							account.notificationSettings?.telegram?.connectionId ||
							account.telegramNotificationId,
						types: account.notificationSettings?.telegram?.types || 'all',
					},
					dashboard: {
						enabled:
							account.notificationSettings?.dashboard?.enabled !== undefined
								? account.notificationSettings.dashboard.enabled
								: true,
						types:
							account.notificationSettings?.dashboard?.types || 'replies_only',
					},
				}
			})
			setTelegramSettings(initialSettings)
			setUnifiedNotificationSettings(initialUnifiedSettings)
		}
	}, [accounts])

	const fetchTelegramConnections = async () => {
		try {
			const response = await api.get('/api/telegram/status')

			if (response.data.success) {
				setTelegramConnections(response.data.connections)
			}
		} catch (error) {
			console.error('Error fetching Telegram connections:', error)
		}
	}

	const handleUnifiedNotificationChange = async (
		accountId,
		destination,
		field,
		value
	) => {
		try {
			const currentSettings = unifiedNotificationSettings[accountId]
			const updatedSettings = {
				...currentSettings,
				[destination]: {
					...currentSettings[destination],
					[field]: value,
				},
			}

			const response = await api.put(
				`/api/email/accounts/${accountId}/notification-settings`,
				{ notificationSettings: updatedSettings }
			)

			if (response.data.success) {
				setUnifiedNotificationSettings(prev => {
					const newState = {
						...prev,
						[accountId]: updatedSettings,
					}

					return newState
				})
			}
		} catch (error) {
			console.error('Error updating notification settings:', error)
			setError('Failed to update notification settings')
			setShowError(true)
			setTimeout(() => setShowError(false), 5000)
		}
	}

	const handleNotificationTypeChange = (accountId, destination, type) => {
		handleUnifiedNotificationChange(accountId, destination, 'types', type)
	}

	const openNotificationModal = accountId => {
		const account = accounts.find(acc => acc._id === accountId)
		setCurrentAccountId(accountId)
		setCurrentAccount(account)
		setShowNotificationModal(true)
	}

	const handleTelegramSettingChange = async (
		accountId,
		telegramConnectionId
	) => {
		try {
			const selectedConnection = telegramConnections.find(
				conn => conn.id === telegramConnectionId
			)
			console.log('DEBUG - Selected Telegram connection for notification:', {
				accountId,
				telegramConnectionId,
				selectedConnection: {
					id: selectedConnection?.id,
					chatType: selectedConnection?.chatType,
					chatTitle: selectedConnection?.chatTitle,
					chatId: selectedConnection?.chatId || 'NO CHAT ID',
					firstName: selectedConnection?.firstName,
					lastName: selectedConnection?.lastName,
				},
			})

			const response = await api.put(
				`/api/email/accounts/${accountId}/telegram-notification`,
				{
					telegramConnectionId:
						telegramConnectionId === 'none' ? null : telegramConnectionId,
				}
			)

			if (response.data.success) {
				setTelegramSettings(prev => ({
					...prev,
					[accountId]:
						telegramConnectionId === 'none' ? null : telegramConnectionId,
				}))
			}
		} catch (error) {
			console.error('Error updating Telegram notification setting:', error)
			setError('Failed to update Telegram notification setting')
			setShowError(true)
			setTimeout(() => setShowError(false), 5000)
		}
	}

	const getAvatarUrl = account => {
		if (account.picture) {
			const cleanedPictureUrl = account.picture.split('=')[0]
			return cleanedPictureUrl
		}

		const emailHash = md5(account.email.toLowerCase().trim())
		const gravatarUrl = `https://www.gravatar.com/avatar/${emailHash}?d=mp&s=80`
		return gravatarUrl
	}

	const getProviderIcon = account => {
		if (account.provider === 'outlook') {
			return outlookIcon
		} else if (account.provider === 'gmail') {
			return gmailIcon
		}
		return null
	}

	const getProviderDisplay = account => {
		const providerLabels = {
			zoho: 'Zoho Mail',
			yahoo: 'Yahoo Mail',
			godaddy: 'GoDaddy',
			smtp: 'SMTP',
			gmail: 'Gmail',
			outlook: 'Outlook'
		}
		return providerLabels[account.provider] || account.provider
	}

	const handleDeleteAccount = async accountId => {
		if (
			!window.confirm('Are you sure you want to delete this email account?')
		) {
			return
		}

		setDeletingAccountId(accountId)
		setError(null)

		try {
			await onDeleteAccount(accountId)
		} catch (err) {
			setError(err.message || 'Failed to revoke access')
			setShowError(true)

			setTimeout(() => setShowError(false), 5000)
		} finally {
			setDeletingAccountId(null)
		}
	}

	return (
		<>
			{/* Error Notification */}
			<div
				aria-live="assertive"
				className="pointer-events-none fixed inset-0 flex items-start justify-center pt-6 z-50"
			>
				<div className="flex w-full flex-col items-center">
					<Transition
						show={showError}
						enter="transform ease-out duration-300 transition"
						enterFrom="translate-y-2 opacity-0"
						enterTo="translate-y-0 opacity-100"
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ${
							isLight
								? 'bg-white ring-1 ring-gray-200'
								: 'bg-[#15202b] ring-1 ring-white/10'
						}`}>
							<div className="p-4">
								<div className="flex items-start">
									<div className="shrink-0">
										<XMarkIcon
											className={`h-6 w-6 ${isLight ? 'text-red-600' : 'text-red-400'}`}
											aria-hidden="true"
										/>
									</div>
									<div className="ml-3 w-0 flex-1 pt-0.5">
										<p className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>{error}</p>
									</div>
									<div className="ml-4 flex shrink-0">
										<button
											type="button"
											onClick={() => setShowError(false)}
											className={`inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
												isLight
													? 'bg-white text-gray-500 hover:text-gray-700'
													: 'bg-[#15202b] text-gray-400 hover:text-gray-300'
											}`}
										>
											<span className="sr-only">Close</span>
											<XMarkIcon aria-hidden="true" className="h-5 w-5" />
										</button>
									</div>
								</div>
							</div>
						</div>
					</Transition>
				</div>
			</div>

			<ul className="space-y-3">
				{accounts.map(account => (
					<li
						key={account._id}
						className={`p-4 rounded-lg border transition-all duration-200 ${
							isLight
								? 'bg-gray-50 border-gray-200 hover:border-gray-300'
								: 'bg-[#263340] border-[#374151] hover:border-[#4B5563]'
						}`}
					>
						<div className="flex flex-col space-y-3">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<div className="flex items-center space-x-3">
									<div className={`w-10 h-10 rounded-full overflow-hidden shrink-0 ${isLight ? 'bg-gray-200' : 'bg-[#374151]'}`}>
										<img
											src={getAvatarUrl(account)}
											alt={account.email}
											className="w-full h-full object-cover"
											referrerPolicy="no-referrer"
											onError={e => {
												console.log('Image load error for:', account.email)
												e.target.onerror = null
												e.target.src = `https://www.gravatar.com/avatar/${md5(
													account.email.toLowerCase().trim()
												)}?d=mp&s=80`
											}}
										/>
									</div>
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-x-2">
											<span className={`font-medium truncate ${isLight ? 'text-gray-900' : 'text-white'}`}>
												{account.email}
											</span>
											{account.provider && (
												<div className="flex items-center">
													{getProviderIcon(account) ? (
														<img
															src={getProviderIcon(account)}
															alt={account.provider}
															className="w-4 h-4 mr-1"
														/>
													) : (
														<svg
															className={`w-4 h-4 mr-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
															/>
														</svg>
													)}
													<span className={`text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
														{getProviderDisplay(account)}
													</span>
													<span className={`text-sm ml-2 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
														• Added{' '}
														{new Date(account.createdAt).toLocaleDateString()}
													</span>
												</div>
											)}
										</div>
										<div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm">
											<div className="flex items-center space-x-2">
												<div
													className={`w-2 h-2 rounded-full ${
														account.canSendEmail ? 'bg-green-500' : 'bg-red-500'
													}`}
												/>
												<span
													className={
														account.canSendEmail
															? (isLight ? 'text-green-600' : 'text-green-400')
															: (isLight ? 'text-red-600' : 'text-red-400')
													}
												>
													Email Send {account.canSendEmail ? 'Enabled' : 'Disabled'}
												</span>
											</div>
											{(account.provider === 'gmail' || account.provider === 'outlook') && (
												<div className="flex items-center space-x-2">
													<div
														className={`w-2 h-2 rounded-full ${
															account.canReadEmail ? 'bg-green-500' : 'bg-red-500'
														}`}
													/>
													<span
														className={
															account.canReadEmail
																? (isLight ? 'text-green-600' : 'text-green-400')
																: (isLight ? 'text-red-600' : 'text-red-400')
														}
													>
														Email Read {account.canReadEmail ? 'Enabled' : 'Disabled'}
													</span>
													{account.canReadEmail && isDevUser && (
														<button
															onClick={() => openNotificationModal(account._id)}
															className={`ml-1 flex items-center space-x-1 px-2 py-0.5 rounded transition-colors text-xs ${
																isLight
																	? 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
																	: 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
															}`}
															title="Notification Settings"
														>
															<span>Notifications</span>
															<BellIcon className="h-3.5 w-3.5" />
														</button>
													)}
												</div>
											)}
										</div>
									</div>
								</div>
								<div className="flex flex-wrap gap-2">
									{(!account.canSendEmail || !account.canReadEmail) &&
									 (account.provider === 'gmail' || account.provider === 'outlook') && (
										<button
											onClick={() =>
												onReAuthorization(account.provider, account.email)
											}
											className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${
												isLight
													? 'text-red-600 bg-red-100 hover:bg-red-200'
													: 'text-red-400 bg-red-900/30 hover:bg-red-900/50'
											}`}
										>
											Re-authorize
										</button>
									)}
									<button
										onClick={() => handleDeleteAccount(account._id)}
										disabled={deletingAccountId === account._id}
										className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${
											isLight
												? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
												: 'text-gray-300 bg-[#374151] hover:bg-[#4B5563]'
										}`}
									>
										{deletingAccountId === account._id ? (
											<>
												<svg
													className={`animate-spin -ml-1 mr-2 h-4 w-4 ${isLight ? 'text-gray-700' : 'text-white'}`}
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													></circle>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													></path>
												</svg>
												<span>Revoking...</span>
											</>
										) : (
											<span>Revoke Access</span>
										)}
									</button>
								</div>
							</div>

							<div className="flex sm:hidden flex-wrap items-center gap-x-4 gap-y-2 text-sm">
								<div className="flex items-center space-x-2">
									<div
										className={`w-2 h-2 rounded-full ${
											account.canSendEmail ? 'bg-green-500' : 'bg-red-500'
										}`}
									/>
									<span
										className={
											account.canSendEmail
												? (isLight ? 'text-green-600' : 'text-green-400')
												: (isLight ? 'text-red-600' : 'text-red-400')
										}
									>
										Email Send {account.canSendEmail ? 'Enabled' : 'Disabled'}
									</span>
								</div>
								{(account.provider === 'gmail' || account.provider === 'outlook') && (
									<div className="flex items-center space-x-2">
										<div
											className={`w-2 h-2 rounded-full ${
												account.canReadEmail ? 'bg-green-500' : 'bg-red-500'
											}`}
										/>
										<span
											className={
												account.canReadEmail
													? (isLight ? 'text-green-600' : 'text-green-400')
													: (isLight ? 'text-red-600' : 'text-red-400')
											}
										>
											Email Read {account.canReadEmail ? 'Enabled' : 'Disabled'}
										</span>
										{account.canReadEmail && isDevUser && (
											<button
												onClick={() => openNotificationModal(account._id)}
												className={`ml-2 flex items-center space-x-1 px-2 py-1 rounded transition-colors text-sm ${
													isLight
														? 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
														: 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
												}`}
												title="Notification Settings"
											>
												<span>Notifications</span>
												<BellIcon className="h-4 w-4" />
											</button>
										)}
									</div>
								)}
							</div>
						</div>
					</li>
				))}
			</ul>

			{/* Notification Settings Modal */}
			{showNotificationModal && (
				<>
					<div className="fixed inset-0 bg-black transition-opacity duration-300 opacity-60 z-40"></div>
					<div
						className="fixed inset-0 flex items-center justify-center -mt-32 z-50"
						onClick={() => setShowNotificationModal(false)}
					>
						<div
							className={`rounded-lg p-8 w-[540px] min-h-[500px] h-auto max-h-[90vh] overflow-y-auto relative ${
								isLight
									? 'bg-white ring-1 ring-gray-200 shadow-lg'
									: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
							}`}
							onClick={e => e.stopPropagation()}
						>
							<div className="flex justify-between items-start">
								<div>
									<h2 className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>
										Notification Settings
									</h2>
									{currentAccount && (
										<p className={`text-sm mt-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
											for {currentAccount.email}
										</p>
									)}
								</div>
								<button
									onClick={() => setShowNotificationModal(false)}
									className={`-mt-1 ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}`}
								>
									<svg
										className="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M6 18L18 6M6 6l12 12"
										></path>
									</svg>
								</button>
							</div>

							{currentAccountId &&
								unifiedNotificationSettings[currentAccountId] && (
									<div className="mt-6 space-y-6">
										{/* Browser Notifications */}
										<div className="space-y-6">
											<div className="flex items-center justify-between pr-10">
												<span className={`text-lg ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
													Browser Notifications
												</span>
												<button
													onClick={() => {
														const currentEnabled =
															unifiedNotificationSettings[currentAccountId]
																?.dashboard?.enabled
														const newValue = !currentEnabled

														setUnifiedNotificationSettings(prev => ({
															...prev,
															[currentAccountId]: {
																...prev[currentAccountId],
																dashboard: {
																	...prev[currentAccountId]?.dashboard,
																	enabled: newValue,
																},
															},
														}))

														handleUnifiedNotificationChange(
															currentAccountId,
															'dashboard',
															'enabled',
															newValue
														)
													}}
													className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
														unifiedNotificationSettings[currentAccountId]
															?.dashboard?.enabled
															? 'bg-blue-600'
															: (isLight ? 'bg-gray-300' : 'bg-gray-600')
													}`}
												>
													<span
														className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
															unifiedNotificationSettings[currentAccountId]
																?.dashboard?.enabled
																? 'translate-x-4'
																: 'translate-x-0.5'
														}`}
													/>
												</button>
											</div>

											{unifiedNotificationSettings[currentAccountId]?.dashboard
												?.enabled && (
												<div className="mt-4">
													<div className="flex items-center space-x-6">
														<div className={`text-sm whitespace-nowrap w-24 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
															Get notified for:
														</div>
														<Menu as="div" className="relative flex-1">
															<MenuButton className={`w-80 text-sm rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${
																isLight
																	? 'bg-white border border-gray-300 text-gray-700'
																	: 'bg-[#374151] border border-gray-600 text-gray-300'
															}`}>
																<span>
																	{unifiedNotificationSettings[currentAccountId]
																		?.dashboard?.types === 'all'
																		? 'All emails – Includes every message'
																		: 'Replies only – Responses to your emails'}
																</span>
																<ChevronDownIcon className="h-4 w-4" />
															</MenuButton>

															<Transition
																enter="transition ease-out duration-100"
																enterFrom="transform opacity-0 scale-95"
																enterTo="transform opacity-100 scale-100"
																leave="transition ease-in duration-75"
																leaveFrom="transform opacity-100 scale-100"
																leaveTo="transform opacity-0 scale-95"
															>
																<MenuItems className={`absolute left-0 mt-2 w-full origin-top-left divide-y rounded-md shadow-lg focus:outline-none z-10 ${
																	isLight
																		? 'divide-gray-200 bg-white ring-1 ring-gray-200'
																		: 'divide-gray-600 bg-[#1a2634] ring-1 ring-white/10'
																}`}>
																	<div className="px-1 py-1">
																		<MenuItem>
																			{({ active }) => (
																				<button
																					className={`${
																						active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
																					} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
																						isLight ? 'text-gray-900' : 'text-gray-100'
																					}`}
																					onClick={() =>
																						handleNotificationTypeChange(
																							currentAccountId,
																							'dashboard',
																							'all'
																						)
																					}
																				>
																					All emails - Every email received
																				</button>
																			)}
																		</MenuItem>
																		<MenuItem>
																			{({ active }) => (
																				<button
																					className={`${
																						active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
																					} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
																						isLight ? 'text-gray-900' : 'text-gray-100'
																					}`}
																					onClick={() =>
																						handleNotificationTypeChange(
																							currentAccountId,
																							'dashboard',
																							'replies_only'
																						)
																					}
																				>
																					Replies only - Only replies to sent
																					emails
																				</button>
																			)}
																		</MenuItem>
																	</div>
																</MenuItems>
															</Transition>
														</Menu>
													</div>
												</div>
											)}
										</div>

										{/* Telegram Notifications */}
										<div className={`space-y-6 border-t pt-4 ${isLight ? 'border-gray-200' : 'border-gray-700'}`}>
											<div className="flex items-center justify-between pr-10">
												<div>
													<span className={`text-lg ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
														Telegram Notifications
													</span>
												</div>
												{isDevUser &&
												telegramConnections &&
												telegramConnections.length > 0 ? (
													<button
														onClick={() => {
															const currentEnabled =
																unifiedNotificationSettings[currentAccountId]
																	?.telegram?.enabled
															const newValue = !currentEnabled
															console.log(
																'Toggle clicked - current:',
																currentEnabled,
																'new:',
																newValue
															)

															setUnifiedNotificationSettings(prev => ({
																...prev,
																[currentAccountId]: {
																	...prev[currentAccountId],
																	telegram: {
																		...prev[currentAccountId]?.telegram,
																		enabled: newValue,
																	},
																},
															}))

															handleUnifiedNotificationChange(
																currentAccountId,
																'telegram',
																'enabled',
																newValue
															)
														}}
														className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
															unifiedNotificationSettings[currentAccountId]
																?.telegram?.enabled
																? 'bg-blue-600'
																: (isLight ? 'bg-gray-300' : 'bg-gray-600')
														}`}
													>
														<span
															className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
																unifiedNotificationSettings[currentAccountId]
																	?.telegram?.enabled
																	? 'translate-x-4'
																	: 'translate-x-0.5'
															}`}
														/>
													</button>
												) : null}
											</div>

											{isDevUser &&
											telegramConnections &&
											telegramConnections.length > 0 ? (
												<>
													{unifiedNotificationSettings[currentAccountId]
														?.telegram?.enabled && (
														<div className="space-y-10 mt-4">
															<div className="flex items-center space-x-6">
																<div className={`text-sm whitespace-nowrap w-24 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																	Get notified for:
																</div>
																<Menu as="div" className="relative flex-1">
																	<MenuButton className={`w-80 text-sm rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${
																		isLight
																			? 'bg-white border border-gray-300 text-gray-700'
																			: 'bg-[#374151] border border-gray-600 text-gray-300'
																	}`}>
																		<span>
																			{unifiedNotificationSettings[
																				currentAccountId
																			]?.telegram?.types === 'all'
																				? 'All emails - Every email received'
																				: 'Replies only - Only replies to sent emails'}
																		</span>
																		<ChevronDownIcon className="h-4 w-4" />
																	</MenuButton>

																	<Transition
																		enter="transition ease-out duration-100"
																		enterFrom="transform opacity-0 scale-95"
																		enterTo="transform opacity-100 scale-100"
																		leave="transition ease-in duration-75"
																		leaveFrom="transform opacity-100 scale-100"
																		leaveTo="transform opacity-0 scale-95"
																	>
																		<MenuItems className={`absolute left-0 mt-2 w-full origin-top-left divide-y rounded-md shadow-lg focus:outline-none z-10 ${
																			isLight
																				? 'divide-gray-200 bg-white ring-1 ring-gray-200'
																				: 'divide-gray-600 bg-[#1a2634] ring-1 ring-white/10'
																		}`}>
																			<div className="px-1 py-1">
																				<MenuItem>
																					{({ active }) => (
																						<button
																							className={`${
																								active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
																							} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
																								isLight ? 'text-gray-900' : 'text-gray-100'
																							}`}
																							onClick={() =>
																								handleNotificationTypeChange(
																									currentAccountId,
																									'telegram',
																									'all'
																								)
																							}
																						>
																							All emails - Every email received
																						</button>
																					)}
																				</MenuItem>
																				<MenuItem>
																					{({ active }) => (
																						<button
																							className={`${
																								active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
																							} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
																								isLight ? 'text-gray-900' : 'text-gray-100'
																							}`}
																							onClick={() =>
																								handleNotificationTypeChange(
																									currentAccountId,
																									'telegram',
																									'replies_only'
																								)
																							}
																						>
																							Replies only - Only replies to
																							sent emails
																						</button>
																					)}
																				</MenuItem>
																			</div>
																		</MenuItems>
																	</Transition>
																</Menu>
															</div>

															<div className="flex items-center space-x-6">
																<div className={`text-sm whitespace-nowrap w-24 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																	Select chat:
																</div>
																<select
																	value={
																		unifiedNotificationSettings[
																			currentAccountId
																		]?.telegram?.connectionId || ''
																	}
																	onChange={e =>
																		handleUnifiedNotificationChange(
																			currentAccountId,
																			'telegram',
																			'connectionId',
																			e.target.value
																		)
																	}
																	className={`w-80 text-sm rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
																		isLight
																			? 'bg-white border border-gray-300 text-gray-700'
																			: 'bg-[#374151] border border-gray-600 text-gray-300'
																	}`}
																>
																	<option value="">
																		Choose notification destination
																	</option>
																	{telegramConnections.map(connection => (
																		<option
																			key={connection.id}
																			value={connection.id}
																		>
																			{connection.chatType === 'private'
																				? `${connection.firstName || ''} ${
																						connection.lastName || ''
																				  }`.trim() || 'Personal Chat'
																				: connection.chatTitle ||
																				  `${connection.chatType} Chat`}
																			{connection.username &&
																				` (@${connection.username})`}
																			{` - ${connection.chatType}`}
																		</option>
																	))}
																</select>
															</div>
														</div>
													)}
													{!unifiedNotificationSettings[currentAccountId]
														?.telegram?.enabled && (
														<div className="mt-4">
															<div className={`text-sm mb-3 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																Enable Telegram notifications above to configure
																settings
															</div>
														</div>
													)}
												</>
											) : (
												<div className="mt-4">
													<div className={`text-sm mb-4 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
														Connect your Telegram to receive email notifications
													</div>
													<button
														onClick={() =>
															(window.location.href = '/settings/telegram')
														}
														className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
													>
														<svg
															className="w-4 h-4"
															fill="currentColor"
															viewBox="0 0 24 24"
														>
															<path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
														</svg>
														<span>Connect Telegram</span>
													</button>
												</div>
											)}
										</div>
									</div>
								)}

							<div className="absolute bottom-8 right-8">
								<button
									onClick={() => setShowNotificationModal(false)}
									className={`px-5 py-2.5 rounded-lg transition-colors duration-150 ease-in-out text-sm font-semibold ${
										isLight
											? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
											: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
									}`}
								>
									Done
								</button>
							</div>
						</div>
					</div>
				</>
			)}
		</>
	)
}

export default EmailAccountList
